import { getAudioContext } from './synth';

export interface MetronomeBeat {
  beat: number;
  time: number;
  accent: boolean;
}

export interface MetronomeConfig {
  bpm: number;
  beatsPerBar: number;
  accentFirst: boolean;
  onBeat: (beat: MetronomeBeat) => void;
}

/**
 * Metrônomo com agendamento antecipado (lookahead) para manter a pulsação
 * estável mesmo quando a interface oscila.
 */
export class Metronome {
  private ctx: AudioContext | null = null;

  private bpm: number;

  private beatsPerBar: number;

  private accentFirst: boolean;

  private onBeat: (beat: MetronomeBeat) => void;

  private timerId: number | null = null;

  private nextNoteTime = 0;

  private beat = 0;

  private readonly lookaheadMs = 25;

  private readonly scheduleAhead = 0.12;

  constructor(config: MetronomeConfig) {
    this.bpm = config.bpm;
    this.beatsPerBar = config.beatsPerBar;
    this.accentFirst = config.accentFirst;
    this.onBeat = config.onBeat;
  }

  private ensureContext(): boolean {
    this.ctx = getAudioContext();
    if (!this.ctx) return false;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return true;
  }

  private scheduleClick(beat: number, time: number): void {
    if (!this.ctx) return;
    const accent = this.accentFirst && beat === 0;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = accent ? 1500 : 950;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(accent ? 0.34 : 0.22, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.06);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(time);
    osc.stop(time + 0.08);
    this.onBeat({ beat, time, accent });
  }

  private scheduler = (): void => {
    if (!this.ctx) return;
    while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAhead) {
      this.scheduleClick(this.beat, this.nextNoteTime);
      this.nextNoteTime += 60 / this.bpm;
      this.beat = (this.beat + 1) % this.beatsPerBar;
    }
  };

  /** Toca um clique isolado imediatamente (usado em prévia). */
  previewTick(accent = true): void {
    if (!this.ensureContext() || !this.ctx) return;
    this.scheduleClick(accent ? 0 : 1, this.ctx.currentTime + 0.02);
  }

  start(): void {
    if (this.timerId !== null) return;
    if (!this.ensureContext() || !this.ctx) return;
    this.beat = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.08;
    this.scheduler();
    this.timerId = window.setInterval(this.scheduler, this.lookaheadMs);
  }

  stop(): void {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
    this.beat = 0;
  }

  get isRunning(): boolean {
    return this.timerId !== null;
  }

  get bpmValue(): number {
    return this.bpm;
  }

  setBpm(bpm: number): void {
    this.bpm = Math.min(240, Math.max(30, Math.round(bpm)));
  }

  setBeatsPerBar(beats: number): void {
    this.beatsPerBar = Math.max(1, Math.round(beats));
  }
}